import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { formatDate } from '@/dayjs'
import type { MarqueeExportRecord } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/**
 * Post-download banner: the CSV was generated and persisted server-side, so it
 * can be re-downloaded as-is (no re-run) even if a new pending walk-in appears.
 */
export function LastExportBanner({
	record,
	onRedownload,
	isRedownloading,
	timeZone
}: {
	record: MarqueeExportRecord
	onRedownload: () => void
	isRedownloading: boolean
	/** The event's IANA zone: provenance is read in the market's clock. */
	timeZone?: string
}) {
	const { t } = useTranslation()
	const excluded = record.excluded ?? 0
	return (
		<div className='flex l:flex-row flex-col l:items-start l:justify-between gap-4 rounded-airbnb-2xl border border-success-300 bg-success-50 px-6 py-5'>
			<div className='flex flex-col gap-1 text-sm'>
				<p className='font-medium text-neutral-900'>
					{t('marquee:export_last_summary', {
						rows: record.rows ?? 0,
						excluded
					})}
				</p>
				<p className='font-medium text-neutral-700'>
					{t('marquee:export_last_meta', {
						by: record.exported_by ?? '-',
						at: formatDate(record.exported_at, 'lll', timeZone),
						file: record.filename ?? '-'
					})}
				</p>
				<p className='max-w-3xl text-neutral-500'>
					{excluded > 0 &&
						`${t('marquee:export_last_audit', { count: excluded })} `}
					{t('marquee:export_last_verify')}
				</p>
			</div>
			<Button
				aria-busy={isRedownloading}
				className='shrink-0'
				disabled={isRedownloading}
				onClick={onRedownload}
				size='sm'
				type='button'
				variant='secondaryOutline'
			>
				{isRedownloading && <Spinner aria-hidden size={16} />}
				{t('marquee:export_redownload')}
			</Button>
		</div>
	)
}
